import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { LoadManager, LoadInformation } from '@/services/Loaders';
import { exportGlb } from '@/services/Exporters';
import { disposeObject3D } from '@/utils/convertUploadedModel';

interface UseModelUploadReturn {
  modelFile: File | null;
  modelUrl: string;
  nonGltfModel: THREE.Object3D | null;
  preparedGlb: Blob | null;
  setModelFile: (file: File | null) => void;
  setModelUrl: (url: string) => void;
  setNonGltfModel: (model: THREE.Object3D | null) => void;
  /** Apply an already-converted model without re-running the file effect (batch create). */
  applyPreparedModel: (
    file: File | null,
    url: string,
    nonGltfModel: THREE.Object3D | null,
    preparedGlb?: Blob | null
  ) => void;
}

export function useModelUpload(): UseModelUploadReturn {
  const { t } = useTranslation();
  const [modelFile, setModelFile] = useState<File | null>(null);
  const [modelUrl, setModelUrlState] = useState<string>('');
  const [nonGltfModel, setNonGltfModel] = useState<THREE.Object3D | null>(null);
  const [preparedGlb, setPreparedGlb] = useState<Blob | null>(null);
  const skipNextFileEffectRef = useRef(false);
  const modelUrlRef = useRef('');
  const ownsModelUrlRef = useRef(false);

  const replaceModelUrl = useCallback((url: string, ownsUrl: boolean) => {
    const previous = modelUrlRef.current;
    if (previous && previous !== url && ownsModelUrlRef.current && previous.startsWith('blob:')) {
      URL.revokeObjectURL(previous);
    }
    modelUrlRef.current = url;
    ownsModelUrlRef.current = ownsUrl;
    setModelUrlState(url);
  }, []);

  const setModelUrl = useCallback(
    (url: string) => {
      replaceModelUrl(url, false);
    },
    [replaceModelUrl]
  );

  const applyPreparedModel = useCallback(
    (
      file: File | null,
      url: string,
      nextNonGltf: THREE.Object3D | null,
      nextPreparedGlb: Blob | null = null
    ) => {
      skipNextFileEffectRef.current = true;
      setModelFile(file);
      setModelUrl(url);
      setNonGltfModel(nextNonGltf);
      setPreparedGlb(nextPreparedGlb);
    },
    [setModelUrl]
  );

  useEffect(
    () => () => {
      const url = modelUrlRef.current;
      if (ownsModelUrlRef.current && url.startsWith('blob:')) URL.revokeObjectURL(url);
    },
    []
  );

  useEffect(() => {
    if (skipNextFileEffectRef.current) {
      skipNextFileEffectRef.current = false;
      return;
    }
    if (!modelFile) {
      setPreparedGlb(null);
      setNonGltfModel(null);
      return;
    }

    const fileExtension = LoadInformation.getFileExtension(modelFile.name).toLocaleLowerCase();
    let cancelled = false;
    setPreparedGlb(null);

    if (fileExtension === '.glb' || fileExtension === '.gltf') {
      replaceModelUrl(URL.createObjectURL(modelFile), true);
      setNonGltfModel(null);
      setPreparedGlb(modelFile);
      return;
    }

    LoadManager.loadModel(
      new LoadInformation(modelFile),
      (model) => {
        if (cancelled) {
          disposeObject3D(model);
          return;
        }
        exportGlb(model, 'model.glb')
          .then((blob) => {
            if (cancelled || !blob) return;
            setPreparedGlb(blob);
            replaceModelUrl(URL.createObjectURL(blob), true);
          })
          .catch((err) => {
            console.error('GLB export failed:', err);
            if (!cancelled) {
              notifications.show({
                title: t('editProduct.system'),
                message: t('editProduct.modelPreparationError'),
                color: 'red',
              });
            }
          })
          .finally(() => {
            disposeObject3D(model);
            if (!cancelled) setNonGltfModel(null);
          });
      },
      undefined,
      (error) => {
        console.error('Model load failed:', error);
        if (!cancelled) {
          notifications.show({
            title: t('editProduct.system'),
            message: t('editProduct.modelPreparationError'),
            color: 'red',
          });
        }
      }
    );

    return () => {
      cancelled = true;
    };
    // Intentionally only re-run when the selected file changes.
  }, [modelFile, replaceModelUrl, t]);

  return {
    modelFile,
    modelUrl,
    nonGltfModel,
    preparedGlb,
    setModelFile,
    setModelUrl,
    setNonGltfModel,
    applyPreparedModel,
  };
}
